// src/pages/accounts/PaymentEntry.js

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";

const EXPENSE_CATEGORIES = [
  "Travel / Fuel", "Food / Meals", "Labour / Helper",
  "Office Supplies", "Utilities", "Rent", "Miscellaneous",
];
const INCOME_CATEGORIES = [
  "Service Revenue", "AMC Revenue", "Installation",
  "Project Revenue", "Consultation", "Material Sale",
  "Other Income",
];

export default function PaymentEntry() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || localStorage.getItem("company_id") || "";

  const [form, setForm] = useState({
    type:           "expense",
    category:       "",
    description:    "",
    amount:         "",
    payment_mode:   "cash",
    status:         "paid",
    project_id:     "",
    site_id:        "",
    site_name:      "",
    reference_no:   "",
    notes:          "",
  });

  const [sites,   setSites]   = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchSites(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSites = async () => {
    if (!companyId) return;
    try {
      const snap = await getDocs(
        query(collection(db, "sites"), where("company_id", "==", companyId))
      );
      setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    setForm(prev => ({
      ...prev,
      site_id:   siteId,
      site_name: site?.site_name || "",
    }));
  };

  const handleSubmit = async () => {
    if (!form.category.trim()) { alert("Category is required"); return; }
    if (!form.amount || Number(form.amount) <= 0) { alert("Amount must be greater than 0"); return; }

    try {
      setLoading(true);
      await addDoc(collection(db, "financial_transactions"), {
        company_id:      companyId,
        type:            form.type,
        sub_type:        "manual_entry",
        category:        form.category,
        description:     form.description || form.category,
        amount:          Number(form.amount),
        payment_mode:    form.payment_mode,
        status:          form.status,
        site_id:         form.site_id   || "",
        site_name:       form.site_name || "",
        reference_no:    form.reference_no || "",
        notes:           form.notes || "",
        created_by:        user.uid || user.id || "",
created_by_name:   user.name || "",
requested_by:      user.uid || user.id || "",
requested_by_name: user.name || "",
approved_by:       user.uid || user.id || "",
approved_by_name:  user.name || "",
balance_amount:    form.status === "paid" ? 0 : Number(form.amount),
paid_amount:       form.status === "paid" ? Number(form.amount) : 0,
        created_at:      serverTimestamp(),
        updated_at:      serverTimestamp(),
      });

      alert("✅ Payment entry saved");
      setForm({
        type: "expense", category: "", description: "", amount: "",
        payment_mode: "cash", status: "paid",
        project_id: "", site_id: "", site_name: "", reference_no: "", notes: "",
      });
    } catch (err) {
      console.error(err);
      alert("Error saving payment entry");
    } finally { setLoading(false); }
  };

  const categories = form.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-xl font-bold text-slate-800 mb-5">Manual Payment Entry</h1>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 max-w-2xl">

        <div className="space-y-4">

          {/* Type */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Entry Type</label>
            <div className="flex gap-2">
              {["expense", "income"].map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, type: t, category: "" }))}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                    form.type === t
                      ? t === "income" ? "bg-green-600 text-white" : "bg-red-600 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}>
                  {t === "income" ? "📈 Income" : "📉 Expense"}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Category *</label>
            <select name="category" value={form.category} onChange={handleChange}
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none">
              <option value="">Select Category</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Description</label>
            <input name="description" value={form.description} onChange={handleChange}
              placeholder="What is this for?"
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none" />
          </div>

          {/* Amount + Mode */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Amount ₹ *</label>
              <input type="number" name="amount" min="1" value={form.amount} onChange={handleChange}
                placeholder="0.00"
                className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Payment Mode</label>
              <select name="payment_mode" value={form.payment_mode} onChange={handleChange}
                className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none">
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Payment Status</label>
            <div className="flex gap-2">
              {[
                { val: "paid",            label: "✅ Paid" },
                { val: "payment_pending", label: "⏳ Pending" },
              ].map(({ val, label }) => (
                <button key={val} onClick={() => setForm(p => ({ ...p, status: val }))}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                    form.status === val ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"
                  }`}>{label}</button>
              ))}
            </div>
          </div>

          {/* Site */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Site <span className="font-normal normal-case text-slate-400">(optional)</span></label>
            <select value={form.site_id} onChange={e => handleSiteChange(e.target.value)}
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none">
              <option value="">Select Site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
            </select>
          </div>

          {/* Reference + Notes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Reference No</label>
              <input name="reference_no" value={form.reference_no} onChange={handleChange}
                placeholder="Bill no / Invoice no"
                className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Notes</label>
              <input name="notes" value={form.notes} onChange={handleChange}
                placeholder="Optional notes"
                className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none" />
            </div>
          </div>

          <button onClick={handleSubmit} disabled={loading}
            className={`w-full py-3.5 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition ${
              form.type === "income" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"
            }`}>
            {loading ? "Saving..." : `Save ${form.type === "income" ? "Income" : "Expense"} Entry`}
          </button>
        </div>
      </div>
    </div>
  );
}
